import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useCatalogEvent } from 'lib/analytics/catalog'
import type { CatalogEventParams } from 'shared-code/app-events'
import { createPortal } from 'react-dom'
import { useRouter } from 'next/router'
import clsx from 'clsx'
import styles from './DealHuntSearchBox.module.css'
import styleUtils from 'shared-components/styles/util.module.css'
import Linked from 'shared-components/service/Linked'
import ScoreRing from 'shared-components/ui/ScoreRing'
import HighlightText from 'components/common/HighlightText'
import SuggestProductSections from './SuggestProductSections'
import Magnifier from 'shared-svg/Magnifier.svg'
import { ThemeContext } from 'shared-components/contexts/ThemeContext'
import { fetchDefaultSuggest, fetchSuggest, prefetchDefaultSuggest } from 'lib/dealhunt/client'
import { parseSgCoverage } from 'lib/dealhunt/sgCoverage'
import { sponsoredRowLabel, splitDefaultSuggest } from 'lib/dealhunt/sponsored'
import { hitNodeId } from 'lib/community/model'
import { useHeaderNav } from 'lib/header-nav/use-header-nav'
import { brandedTitle, categoryLeafLabel, dealHuntProductHref, formatCents } from 'lib/dealhunt/url'
import { EMPTY_SUGGEST } from 'lib/dealhunt/constants'
import { publicConfig } from 'config'
import type { DealHuntSuggestHit, DealHuntSuggestResponse, SgPostHit } from 'lib/dealhunt/types'

interface DealHuntSearchBoxProps {
  className?: string
  initialQuery?: string
  placeholder?: string
  /** `desktop` = portal dropdown (header), `mobile` = full-screen overlay (drawer). */
  variant?: 'desktop' | 'mobile'
  /** Mobile only: close the drawer when a result is followed. */
  onNavigate?: () => void
}

interface DropdownPos {
  left: number
  top: number
  width: number
}

/** Deepest category leaf for a hit's subtitle (brand goes in the title instead). */
function hitSubtitle(hit: DealHuntSuggestHit): string | null {
  return categoryLeafLabel(
    hit['publicCategoryPath.lvl3']?.[0] ??
      hit['publicCategoryPath.lvl2']?.[0] ??
      hit['publicCategoryPath.lvl1']?.[0]
  )
}

/** "{brand} {name}", skipping the prefix when the name already starts with it. */
function hitTitle(hit: DealHuntSuggestHit): string {
  return brandedTitle(hit.brand, hit.canonicalName || hit.title)
}

/** Top-level `sgReviewUpdatedAt` is epoch SECONDS (it predates the sgCoverage
 *  contract's millis convention). Month granularity ("Aug 2026") is all the
 *  payload honestly supports. */
function formatReviewUpdated(seconds: number | null | undefined): string | null {
  if (typeof seconds !== 'number' || seconds <= 0) {
    return null
  }
  const date = new Date(seconds * 1000)
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

/** Flat GA4 param for the results event: which coverage kinds the top hit's
 *  block renders — the coverage-block CTR denominator ('' = no block). */
function coverageKinds(hit: DealHuntSuggestHit | undefined): string {
  if (!hit) {
    return ''
  }
  const { coverage } = parseSgCoverage(hit.sgCoverage)
  if (!coverage) {
    return ''
  }
  return [
    coverage.review ? 'review' : null,
    coverage.versus ? 'versus' : null,
    coverage.best && coverage.best.length > 0 ? 'best' : null,
  ]
    .filter(Boolean)
    .join(',')
}

/** Eyebrow label per post type. `opinion` reads "Feature" — the WP source bucket is
 *  "Features & Opinions" and that's the label soundguys.com shows. */
const POST_TYPE_LABELS: Record<SgPostHit['type'], string> = {
  review: 'Review',
  versus: 'Versus',
  opinion: 'Feature',
}

/** Result-kind tabs across the top of the dropdown — one kind at a time,
 *  products first. */
type SuggestTab = 'products' | 'accessories' | 'articles'

/** Tab count chip: totals are estimates, and past 99 the exact number stops
 *  informing the click — cap the label. */
function tabCount(n: number | undefined): string | null {
  if (n === undefined || n <= 0) {
    return null
  }
  return n > 99 ? '99+' : String(n)
}

const DealHuntSearchBox: React.VFC<DealHuntSearchBoxProps> = ({
  className,
  initialQuery = '',
  placeholder = 'Search audio products or reviews…',
  variant = 'desktop',
  onNavigate,
}) => {
  const isMobile = variant === 'mobile'
  const router = useRouter()
  const { doc: navDoc } = useHeaderNav()
  const { theme } = ThemeContext.useContainer()
  const trailingSlash = publicConfig('useTrailingSlash')
  const [query, setQuery] = useState(initialQuery)
  const [tab, setTab] = useState<SuggestTab>('products')
  const [open, setOpen] = useState(false)
  const [result, setResult] = useState<DealHuntSuggestResponse>(EMPTY_SUGGEST)
  const [loading, setLoading] = useState(false)
  const [pos, setPos] = useState<DropdownPos | null>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const overlayInputRef = useRef<HTMLInputElement>(null)

  // Desktop anchors a fixed-position portal to the input; mobile is full-screen.
  // The 4b frame's dropdown is wider than the field — it grows rightward to ~640px
  // (trimmed from the frame's ~720 — Guillaume 2026-08-04: the panel read too big),
  // clamped so it never leaves the viewport.
  const updatePos = useCallback(() => {
    if (isMobile || !formRef.current) {
      return
    }
    const rect = formRef.current.getBoundingClientRect()
    const room = document.documentElement.clientWidth - rect.left - 24
    setPos({
      left: rect.left,
      top: rect.bottom + 6,
      width: Math.max(rect.width, Math.min(640, room)),
    })
  }, [isMobile])

  useEffect(() => {
    if (isMobile || !open) {
      return undefined
    }
    updatePos()
    window.addEventListener('scroll', updatePos, true)
    window.addEventListener('resize', updatePos)
    return () => {
      window.removeEventListener('scroll', updatePos, true)
      window.removeEventListener('resize', updatePos)
    }
  }, [isMobile, open, updatePos])

  // Move focus into the overlay input when the mobile takeover opens.
  useEffect(() => {
    if (isMobile && open) {
      overlayInputRef.current?.focus()
    }
  }, [isMobile, open])

  // Each open starts on Products — a kind picked for one query would silently
  // narrow the next one.
  useEffect(() => {
    if (open) {
      setTab('products')
    }
  }, [open])

  const { track } = useCatalogEvent()

  // Lane-0 funnel state: has anything been followed since this open? (Result,
  // coverage, submit, or see-all — tab switches don't count as a destination.)
  const interactedRef = useRef(false)
  // Latest term/count for events fired outside render flow (abandon).
  const latestRef = useRef({ term: '', count: 0 })
  useEffect(() => {
    latestRef.current = { term: query.trim(), count: result.nbHits }
  })

  // One `open` per dropdown open, one `abandon` per close that followed nothing —
  // the funnel ends every open reaches, whichever path closed it (outside click,
  // Escape, back button, navigation).
  const wasOpenRef = useRef(false)
  useEffect(() => {
    if (open) {
      interactedRef.current = false
      track('product_search_typeahead_open', { search_term: latestRef.current.term })
    } else if (wasOpenRef.current && !interactedRef.current) {
      track('product_search_typeahead_abandon', {
        search_term: latestRef.current.term,
        result_count: latestRef.current.count,
      })
    }
    wasOpenRef.current = open
  }, [open, isMobile, track])

  // Typeahead. Closed → nothing. Focused with <2 chars → default recently-reviewed
  // audio (popular-audio fallback when Meili is down).
  // Otherwise debounced suggestions (products + accessories + SoundGuys posts).
  // Each landed response reports a `results` event — the denominator every
  // typeahead click rate needs — with the state that produced it.
  useEffect(() => {
    if (!open) {
      return undefined
    }
    const ctrl = new AbortController()

    const trackResults = (state: 'default' | 'query', res: DealHuntSuggestResponse): void =>
      track('product_search_typeahead_results', {
        search_term: state === 'query' ? query.trim() : '',
        state,
        result_count: res.nbHits,
        accessory_count: res.accessoryNbHits ?? null,
        article_count: res.postNbHits ?? null,
        coverage_kinds: state === 'query' ? coverageKinds(res.hits[0]) : '',
      })

    if (query.trim().length < 2) {
      // Narrowing back below the query threshold: drop the old query's rows now,
      // not when the default response lands — stale "Top results" under a
      // near-empty field misread as results for the shorter query.
      setResult(EMPTY_SUGGEST)
      setLoading(true)
      fetchDefaultSuggest(ctrl.signal)
        .then(res => {
          if (res) {
            setResult(res)
            trackResults('default', res)
          }
        })
        .catch(() => undefined)
        .finally(() => setLoading(false))
      return () => ctrl.abort()
    }

    const timer = setTimeout(() => {
      setLoading(true)
      fetchSuggest(query, ctrl.signal)
        .then(res => {
          if (res) {
            setResult(res)
            trackResults('query', res)
          }
        })
        .catch(() => undefined)
        .finally(() => setLoading(false))
    }, 120)
    return () => {
      clearTimeout(timer)
      ctrl.abort()
    }
  }, [open, query, track])

  useEffect(() => {
    if (isMobile) {
      return undefined
    }
    const onClick = (e: MouseEvent): void => {
      const target = e.target as Node
      if (!formRef.current?.contains(target) && !dropdownRef.current?.contains(target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [isMobile])

  const suffix = trailingSlash ? '/' : ''

  const close = (): void => {
    setOpen(false)
    onNavigate?.()
  }

  const submit = (e: React.SyntheticEvent): void => {
    e.preventDefault()
    const q = query.trim()
    if (q.length === 0) {
      return
    }
    interactedRef.current = true
    // Below the 2-char fetch threshold the visible rows are the DEFAULT state,
    // not results for `q` — reporting their count against q would be a lie.
    const counted = q.length >= 2
    track('product_search_submit', {
      search_term: q,
      result_count: counted ? result.nbHits : null,
      state: counted ? 'query' : 'default',
    })
    void router.push(`/product-search${suffix}?q=${encodeURIComponent(q)}`)
    close()
  }

  // Footer "See all": a typed query goes to /product-search; empty state browses Audio.
  const seeAll = (e: React.SyntheticEvent): void => {
    e.preventDefault()
    const q = query.trim()
    const searching = q.length >= 2
    interactedRef.current = true
    track('product_search_see_all_click', {
      search_term: q,
      result_count: searching ? result.nbHits : null,
      state: searching ? 'query' : 'default',
      destination_url: searching ? '/product-search' : '/product/audio',
    })
    if (searching) {
      void router.push(`/product-search${suffix}?q=${encodeURIComponent(q)}`)
    } else {
      void router.push(`/product/audio${suffix}`)
    }
    close()
  }

  const accessoryHits = result.accessoryHits ?? []
  const postHits = result.postHits ?? []
  const hasQuery = query.trim().length >= 2

  // Empty-query products heading follows the ranking that actually produced the
  // list: the REST fallback can't sort by review recency, so its list stays
  // labeled "Popular products" (defaultSort: 'popular').
  const defaultTitle = result.defaultSort === 'popular' ? 'Popular products' : 'Recently reviewed'

  // The tab row shows whenever the federated Meili path answered (it always
  // carries all three kinds — `accessoryHits`/`postHits` defined), including the
  // default empty-query state. Gating on the *path*, not per-kind hit counts,
  // keeps the row stable while a query narrows: a products-only result empties a
  // tab instead of collapsing the row. Only the REST fallback (products-only,
  // no kinds to switch between) hides it.
  const showTabs = result.accessoryHits !== undefined && result.postHits !== undefined
  const tabs: { id: SuggestTab; label: string; count?: number }[] = [
    { id: 'products', label: 'Products', count: result.nbHits },
    { id: 'accessories', label: 'Accessories', count: result.accessoryNbHits },
    { id: 'articles', label: 'Articles', count: result.postNbHits },
  ]
  const activeTab = showTabs ? tab : 'products'

  const trackTypeahead = (extra: CatalogEventParams): void => {
    interactedRef.current = true
    track('product_search_typeahead_click', {
      search_term: query.trim(),
      tab: activeTab,
      state: hasQuery ? 'query' : 'default',
      ...extra,
    })
  }

  // Default state (contract C2): the sponsored line gets its own "Sponsored" section above
  // "Recently reviewed" and is deduped out of it. A typed query never re-ranks: a sponsored hit
  // that matches stays where relevance put it, with an in-row label only (`sponsoredRowLabel`
  // labels every sponsored row except the one under the header).
  const { sponsoredRows, reviewedRows } = useMemo(() => {
    if (hasQuery) {
      return { sponsoredRows: [], reviewedRows: result.hits }
    }
    return splitDefaultSuggest(result.hits, result.sponsored)
  }, [hasQuery, result.hits, result.sponsored])

  // "From our coverage" under ONE product row only — the sgCoverage block
  // (contract: dealhunt/docs/sg-coverage-product-docs.md): the sponsored row when the
  // Sponsored section shows, else the first products row. Keyed to the
  // product, parsed tolerantly: a drifted shape renders no block and signals.
  // Renders in the default state too: the default list is recently-reviewed
  // products, so the top row always has coverage to showcase (the REST
  // fallback's hits carry no sgCoverage — absent parses to no block, no signal).
  const topHit: DealHuntSuggestHit | undefined = sponsoredRows[0] ?? reviewedRows[0]
  const topParse = useMemo(() => (topHit ? parseSgCoverage(topHit.sgCoverage) : null), [topHit])

  // One sponsored-slot impression per dropdown open, when the Sponsored section is on screen.
  const sponsoredShownRef = useRef<string | null>(null)
  const shownSponsor = open && activeTab === 'products' ? sponsoredRows[0] : undefined
  useEffect(() => {
    if (!open) {
      sponsoredShownRef.current = null
      return undefined
    }
    if (!shownSponsor || sponsoredShownRef.current === shownSponsor.objectID) {
      return undefined
    }
    sponsoredShownRef.current = shownSponsor.objectID
    track('product_search_sponsored_slot_impression', {
      target_id: shownSponsor.sgSponsored?.postId ?? null,
      target_product_id: hitNodeId(shownSponsor),
      target_product_name: hitTitle(shownSponsor),
    })
    return undefined
  }, [open, shownSponsor, track])

  // Degradation must never be silent (a broken shape looks like honest absence) —
  // one signal per product per session, outside the render pass.
  const signaledRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (topHit && topParse?.invalid && !signaledRef.current.has(topHit.objectID)) {
      signaledRef.current.add(topHit.objectID)
      console.warn(`[sgCoverage] drifted shape on product ${topHit.objectID} — block degraded`)
      track('product_search_coverage_invalid', {
        target_product_slug: topHit.fullSlug,
        search_term: latestRef.current.term,
      })
    }
  }, [topHit, topParse, track])

  const trackCoverage = (kind: 'review' | 'versus' | 'best', url: string): void => {
    interactedRef.current = true
    track('product_search_coverage_click', {
      search_term: query.trim(),
      state: hasQuery ? 'query' : 'default',
      kind,
      destination_url: url,
      target_product_name: topHit ? hitTitle(topHit) : null,
      target_product_slug: topHit?.fullSlug ?? null,
    })
  }

  const review = topParse?.coverage?.review
  const versus = topParse?.coverage?.versus
  const bestPick = topParse?.coverage?.best?.[0]
  const reviewMeta =
    review &&
    [
      review.authorNames.length > 0 ? `by ${review.authorNames.join(', ')}` : null,
      formatReviewUpdated(topHit?.sgReviewUpdatedAt)
        ? `updated ${formatReviewUpdated(topHit?.sgReviewUpdatedAt)}`
        : null,
    ]
      .filter(Boolean)
      .join(' · ')

  // Coverage URLs are WP pages on soundguys.com — routes this app doesn't own,
  // so plain <a> full navigations (same rule as post hits below).
  const coverageBlock = topHit && (review || versus || bestPick) && (
    <div className={styles.coverage} key={topHit.objectID}>
      {review && (
        <a
          href={review.url}
          className={styles.coverageRow}
          onClick={() => {
            trackCoverage('review', review.url)
            close()
          }}
        >
          <span className={clsx(styles.coverageTag, styles.coverageTagReview)}>{'Review'}</span>
          <span className={styles.coverageBody}>
            <span className={styles.coverageTitle}>
              {review.title ?? `Our ${hitTitle(topHit)} review`}
            </span>
            {reviewMeta && <span className={styles.coverageMeta}>{reviewMeta}</span>}
          </span>
          {typeof topHit.sgScore === 'number' && (
            <span className={styles.coverageScore}>{`${topHit.sgScore.toFixed(1)}/10`}</span>
          )}
          <span className={styles.coverageArrow} aria-hidden="true">
            {'→'}
          </span>
        </a>
      )}
      {versus && (
        <a
          href={versus.url}
          className={styles.coverageRow}
          onClick={() => {
            trackCoverage('versus', versus.url)
            close()
          }}
        >
          <span className={clsx(styles.coverageTag, styles.coverageTagVersus)}>{'Versus'}</span>
          <span className={styles.coverageBody}>
            <span className={styles.coverageTitle}>{versus.title}</span>
          </span>
          <span className={styles.coverageArrow} aria-hidden="true">
            {'→'}
          </span>
        </a>
      )}
      {bestPick && (
        <a
          href={bestPick.guideUrl}
          className={styles.coverageRow}
          onClick={() => {
            trackCoverage('best', bestPick.guideUrl)
            close()
          }}
        >
          <span className={clsx(styles.coverageTag, styles.coverageTagBest)}>{'Best'}</span>
          <span className={styles.coverageBody}>
            <span className={styles.coverageTitle}>
              {bestPick.role ? `${bestPick.role} · ${bestPick.guideTitle}` : bestPick.guideTitle}
            </span>
          </span>
          <span className={styles.coverageArrow} aria-hidden="true">
            {'→'}
          </span>
        </a>
      )}
    </div>
  )

  const renderHit = (
    hit: DealHuntSuggestHit,
    index: number,
    appendix?: React.ReactNode,
    /** The row sits in the default state's Sponsored section (the slot itself). */
    inSponsoredSection = false
  ): JSX.Element => {
    const subtitle = hitSubtitle(hit)
    const sponsoredLabel = sponsoredRowLabel(hit, inSponsoredSection)
    // A row shown as sponsored — the slot row, or a typed match with the label — reports it.
    const shownSponsored = inSponsoredSection || sponsoredLabel
    const body = (
      <>
        <span className={styles.hitImage}>
          {hit.imageUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={hit.imageUrl} alt="" loading="lazy" />
          ) : (
            <span className={styles.hitImageEmpty} aria-hidden="true" />
          )}
        </span>
        <span className={styles.hitBody}>
          <span className={styles.hitTitle}>
            <HighlightText text={hitTitle(hit)} query={query} />
          </span>
          {(subtitle || sponsoredLabel) && (
            <span className={styles.hitSub}>
              {sponsoredLabel && <span className={styles.sponsoredTag}>{'Sponsored'}</span>}
              {sponsoredLabel && subtitle && ' · '}
              {subtitle}
            </span>
          )}
        </span>
        {formatCents(hit.currentPriceCents, hit.domain) && (
          <span className={styles.hitPrice}>{formatCents(hit.currentPriceCents, hit.domain)}</span>
        )}
        {typeof hit.sgScore === 'number' && (
          <ScoreRing score={hit.sgScore} className={styles.hitRing}>
            {hit.sgScore.toFixed(1)}
          </ScoreRing>
        )}
      </>
    )
    return (
      <li key={hit.objectID}>
        {hit.fullSlug ? (
          <Linked
            href={dealHuntProductHref(hit.fullSlug, hit.brand, trailingSlash, hit.brandSlug)}
            className={styles.hit}
            onClick={() => {
              trackTypeahead({
                result_type: activeTab === 'accessories' ? 'accessory' : 'product',
                target_product_name: hitTitle(hit),
                target_product_slug: hit.fullSlug,
                target_asin: hit.asin,
                position: index + 1,
                ...(shownSponsored ? { kind: 'sponsored_slot' } : {}),
              })
              if (inSponsoredSection) {
                track('product_search_sponsored_slot_click', {
                  target_id: hit.sgSponsored?.postId ?? null,
                  target_product_id: hitNodeId(hit),
                  target_product_name: hitTitle(hit),
                  position: index + 1,
                })
              }
              close()
            }}
          >
            {body}
          </Linked>
        ) : (
          // No routable slug yet: an inert row, not a dead '#' link that would
          // navigate nowhere while still reporting clicks.
          <span className={clsx(styles.hit, styles.hitInert)}>{body}</span>
        )}
        {appendix}
      </li>
    )
  }

  // Editorial posts are WP pages at absolute soundguys.com URLs — routes this app
  // doesn't own, so a plain <a> (full navigation), never Linked/router.push.
  const renderPost = (post: SgPostHit, index: number): JSX.Element => (
    <li key={post.id}>
      <a
        href={post.url}
        className={styles.hit}
        onClick={() => {
          trackTypeahead({
            result_type: post.type,
            target_id: post.postId,
            target_title: post.title,
            destination_url: post.url,
            position: index + 1,
          })
          close()
        }}
      >
        <span className={styles.hitImage}>
          {post.imageUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={post.imageUrl} alt="" loading="lazy" />
          ) : (
            <span className={styles.hitImageEmpty} aria-hidden="true" />
          )}
        </span>
        <span className={styles.hitBody}>
          <span className={styles.hitTitle}>
            <HighlightText text={post.title} query={query} />
          </span>
          <span className={styles.hitSub}>
            <span className={styles.postType}>{POST_TYPE_LABELS[post.type] ?? post.type}</span>
            {post.overallRating !== null && ` · ${post.overallRating.toFixed(1)}/10`}
          </span>
        </span>
      </a>
    </li>
  )

  // Shown in each section's empty slot while the first request is in flight —
  // replaces the old "Searching…" line so the beat reads as loading, not "no results".
  const spinner = <span className={styles.spinner} role="status" aria-label="Searching" />

  const suggestBody = (
    <>
      {showTabs && (
        <div className={styles.tabRow} role="tablist" aria-label="Result type">
          {tabs.map(t => {
            // Empty-query totals are whole-corpus estimates ("99+" everywhere) —
            // counts only inform once they respond to a typed query.
            const count = hasQuery ? tabCount(t.count) : null
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={t.id === activeTab}
                className={clsx(styles.tab, { [styles.tabActive]: t.id === activeTab })}
                onClick={() => {
                  track('product_search_typeahead_tab_click', {
                    search_term: query.trim(),
                    tab: t.id,
                    item_count: t.count ?? null,
                  })
                  setTab(t.id)
                }}
              >
                {t.label}
                {count && <span className={styles.tabCount}>{count}</span>}
              </button>
            )
          })}
        </div>
      )}

      {activeTab === 'products' && (
        <SuggestProductSections
          hasQuery={hasQuery}
          defaultTitle={defaultTitle}
          sponsoredRows={sponsoredRows}
          reviewedRows={reviewedRows}
          coverageBlock={coverageBlock}
          emptyNode={loading ? spinner : 'No matches yet'}
          renderHit={renderHit}
        />
      )}

      {activeTab === 'accessories' && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            {hasQuery ? 'Accessories' : 'Popular accessories'}
          </div>
          {accessoryHits.length > 0 ? (
            <ul className={styles.hits}>{accessoryHits.map((h, i) => renderHit(h, i))}</ul>
          ) : (
            <div className={styles.noHits}>{loading ? spinner : 'No matching accessories'}</div>
          )}
        </div>
      )}

      {activeTab === 'articles' && (
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            {hasQuery ? 'Articles & reviews' : 'Latest articles'}
          </div>
          {postHits.length > 0 ? (
            <ul className={styles.hits}>{postHits.map((p, i) => renderPost(p, i))}</ul>
          ) : (
            <div className={styles.noHits}>{loading ? spinner : 'No matching articles'}</div>
          )}
        </div>
      )}
    </>
  )

  // "See all audio products" is a product-surface exit — on the Articles tab it
  // points away from what's on screen, so it's dropped there. With a live query the
  // label carries that query's own product total; the default state falls back to
  // the catalogue total riding in from the header-nav document.
  const browseAllLabel = navDoc?.audioTotal
    ? `See all ${navDoc.audioTotal.toLocaleString('en-US')} audio products`
    : 'See all audio products'
  const seeAllButton = activeTab !== 'articles' && (
    <button type="button" className={styles.seeAll} onClick={seeAll}>
      <span>
        {hasQuery && result.nbHits > 0
          ? `See all ${result.nbHits.toLocaleString('en-US')} results for “${query.trim()}”`
          : browseAllLabel}
      </span>
      <span className={styles.seeAllArrow} aria-hidden="true">
        {'→'}
      </span>
    </button>
  )

  // Desktop: a fixed portal dropdown anchored under the header input.
  const desktopDropdown = open && pos && typeof document !== 'undefined' && (
    <div
      ref={dropdownRef}
      className={styles.dropdown}
      style={{ left: pos.left, top: pos.top, width: pos.width }}
    >
      {suggestBody}
      {seeAllButton}
    </div>
  )

  // Mobile: a full-screen takeover (back arrow + search, full-bleed results).
  // Portalled to <body> so it escapes the drawer popup's containing block and
  // covers the page header too; carries .darkTheme since it leaves that subtree.
  const mobileOverlay = open && typeof document !== 'undefined' && (
    <div className={clsx(styles.overlay, { [styleUtils.darkTheme]: theme === 'dark' })}>
      <div className={styles.overlayHead}>
        <button
          type="button"
          className={styles.backBtn}
          aria-label="Back"
          onClick={() => setOpen(false)}
        >
          <svg className={styles.backIcon} viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M15 18l-6-6 6-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <form
          className={clsx(styles.form, styles.formMobile, styles.overlayForm)}
          onSubmit={submit}
        >
          <button type="submit" className={styles.iconBtn} aria-label="Search">
            <Magnifier className={styles.searchGlyph} />
          </button>
          <input
            ref={overlayInputRef}
            className={styles.input}
            type="search"
            value={query}
            placeholder={placeholder}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Escape') {
                setOpen(false)
              }
            }}
            aria-label="Search products"
          />
        </form>
      </div>
      <div className={styles.overlayScroll}>{suggestBody}</div>
      {seeAllButton}
    </div>
  )

  return (
    <div className={clsx(styles.root, className)}>
      <form
        ref={formRef}
        className={clsx(styles.form, { [styles.formMobile]: isMobile })}
        onSubmit={submit}
        // Warm the default-suggest cache while the pointer travels to the input,
        // so the first focus paints the dropdown without a loading-spinner beat.
        onPointerEnter={prefetchDefaultSuggest}
        role="search"
      >
        <button type="submit" className={styles.iconBtn} aria-label="Search">
          <Magnifier className={styles.searchGlyph} />
        </button>
        <input
          className={styles.input}
          type="search"
          value={query}
          placeholder={placeholder}
          onChange={e => setQuery(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              setOpen(false)
            }
          }}
          aria-label="Search products"
        />
      </form>
      {isMobile
        ? mobileOverlay && createPortal(mobileOverlay, document.body)
        : desktopDropdown && createPortal(desktopDropdown, document.body)}
    </div>
  )
}

export default DealHuntSearchBox
